import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { SplitSection, EditorialIntro } from '../innovher/SplitSection';
import SEO from '../SEO';
import { STOCK, STUDIO_IMAGES } from '../../lib/images';

const PILLARS = [
  {
    title: 'Work',
    body: 'A professional environment for founders, startups and teams to operate and build.',
  },
  {
    title: 'Connect',
    body: 'Meet entrepreneurs, mentors, professionals, investors and ecosystem stakeholders.',
  },
  {
    title: 'Collaborate',
    body: 'Create opportunities for partnerships, business development, knowledge exchange and cross-ecosystem collaboration.',
  },
  {
    title: 'Learn',
    body: 'Participate in interactions, discussions, expert sessions and community-led learning opportunities.',
  },
  {
    title: 'Grow',
    body: 'Stay connected with Innovher’s broader ecosystem of acceleration, mentorship, business advisory and investor networks.',
  },
];

const AUDIENCE = [
  'Startup founders and co-founders',
  'Early-stage and growing companies',
  'Independent professionals and consultants',
  'Small business teams',
  'Mentors and industry experts',
  'Investors and ecosystem stakeholders',
  'Professionals looking to work within an entrepreneurial environment',
];

function SectionEyebrow({ children }) {
  return (
    <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-4">{children}</div>
  );
}

function PillarsInteractive() {
  const [active, setActive] = useState(0);
  const item = PILLARS[active];

  return (
    <div className="mt-12 grid lg:grid-cols-[0.9fr_1.1fr] gap-6 lg:gap-10 items-start">
      <div className="space-y-2">
        {PILLARS.map((p, i) => {
          const on = i === active;
          return (
            <button
              key={p.title}
              type="button"
              onClick={() => setActive(i)}
              onMouseEnter={() => setActive(i)}
              className={`w-full text-left rounded-2xl border px-5 py-4 transition-all ${on
                ? 'border-[#d42b92]/60 bg-[#d42b92]/12'
                : 'border-white/10 bg-white/[0.02] hover:border-white/25'
                }`}
            >
              <div className="flex items-center gap-4">
                <span
                  className={`text-xs font-semibold tracking-[0.2em] ${on ? 'text-[#ff8ec5]' : 'text-white/30'
                    }`}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className={`text-lg font-semibold ${on ? 'text-white' : 'text-white/65'}`}>
                  {p.title}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div
        key={item.title}
        className="relative rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-[#d42b92]/18 via-white/[0.03] to-transparent p-8 sm:p-10 min-h-[220px] overflow-hidden"
      >
        <div className="absolute -right-8 -top-8 h-36 w-36 rounded-full bg-[#d42b92]/25 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="text-[#ff8ec5] text-xs tracking-[0.28em] uppercase mb-4">More than a workspace</div>
          <h3 className="text-3xl sm:text-4xl font-semibold text-white">{item.title}</h3>
          <p className="mt-5 text-white/65 text-base sm:text-lg leading-relaxed">{item.body}</p>
        </div>
      </div>
    </div>
  );
}

export default function StudioIPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const heroImage = STOCK['studio-i'] || STUDIO_IMAGES[0]?.src;

  return (
    <>
      <SEO
        title="Studio-I | Where Ideas, People and Businesses Come Together"
        description="Studio-I is InnovHer's collaborative workspace and startup ecosystem hub for founders, professionals, mentors, investors and growing teams in Jaipur."
        keywords="Studio-I, Studio i Jaipur, InnovHer workspace, startup ecosystem hub, founder coworking Jaipur"
      />

      {/* Header / Hero Section */}
      <section className="relative bg-[#08080a] pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 lg:pb-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <EditorialIntro
            className="reveal-up"
            eyebrow="Studio-I"
            logo="/new_images/logos/studio-i.webp"
            title={
              <>
                Where ideas, people and businesses{' '}
                <span className="text-[#d42b92]">come together</span>.
              </>
            }
            lead="Innovher’s collaborative workspace and startup ecosystem hub, designed for founders, professionals, mentors, investors and growing teams."
          >
            <p>
              Studio-I is Innovher’s collaborative workspace and startup ecosystem hub, designed
              for founders, professionals, mentors, investors and growing teams to work, meet and
              build within an active business environment.
            </p>
            <p>
              More than a workspace, Studio-I brings together the infrastructure, people and
              ecosystem access that early-stage and growing businesses need to move forward.
            </p>
          </EditorialIntro>
        </div>
      </section>

      {/* Built for founders */}
      <section className="relative bg-[#08080a] py-16 lg:py-24 border-t border-white/5 overflow-hidden">
        <div className="absolute top-1/2 right-0 w-[420px] h-[420px] rounded-full bg-[#d42b92]/10 blur-[140px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left: Text content */}
            <div className="reveal-up">
              <SectionEyebrow>Built for Founders. Designed for Growth.</SectionEyebrow>
              <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                Building a company requires more than a{' '}
                <span className="text-[#d42b92]">desk</span>
              </h2>
              <p className="mt-6 text-white/60 text-base sm:text-lg leading-relaxed">
                Studio-I creates an environment where founders can work alongside other
                entrepreneurs, interact with mentors and industry professionals, conduct business
                meetings and stay connected with Innovher’s wider startup and investment ecosystem.
              </p>
              <p className="mt-4 text-white font-semibold text-lg">
                Whether you are working independently, building a team, meeting stakeholders or
                preparing for your next stage of growth, Studio-I provides a professional space
                designed around the realities of building a business.
              </p>
            </div>

            {/* Right: Image */}
            <div className="reveal-up relative">
              <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60">
                <img
                  src={STUDIO_IMAGES[0]?.src || heroImage}
                  alt="Studio-I collaborative workspace"
                  className="w-full h-full object-cover aspect-[4/3]"
                  style={{ objectPosition: STUDIO_IMAGES[0]?.position || 'center' }}
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#d42b92]/20 to-transparent pointer-events-none" />
              </div>
              <div className="absolute -inset-4 -z-10 rounded-3xl bg-[#d42b92]/8 blur-2xl pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* More than a workspace */}
      <section className="relative bg-[#08080a] py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SplitSection
            className="reveal-up"
            // eyebrow="More Than a Workspace"
            title={
              <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                Value beyond physical{' '}
                <span className="text-[#d42b92]">infrastructure</span>
              </h2>
            }
          >
            <p>
              At Studio-I, the value goes beyond physical infrastructure. Members become part of an
              environment where conversations can turn into collaborations, introductions can open
              new opportunities and founders have regular access to an active entrepreneurial
              community.
            </p>
          </SplitSection>
          <div className="reveal-up">
            <PillarsInteractive />
          </div>
        </div>
      </section>

      {/* Gallery, homepage studio images */}
      <section className="relative bg-[#08080a] py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up max-w-4xl lg:max-w-5xl mb-10">
            <SectionEyebrow>Inside Studio-I</SectionEyebrow>
            <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
              The space where the ecosystem{' '}
              <span className="text-[#d42b92]">meets</span>
            </h2>
          </div>

          <div className="reveal-up columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
            {STUDIO_IMAGES.map((img) => (
              <div
                key={img.id}
                className="break-inside-avoid rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] group"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={img.src}
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ objectPosition: img.position || 'center' }}
                    loading="lazy"
                  />
                </div>
                {/* <div className="px-4 py-3 flex items-center justify-between gap-3">
                  <span className="text-sm text-white/80">{img.title}</span>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#ff8ec5]/80">
                    {img.tag}
                  </span>
                </div> */}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Active ecosystem */}
      <section className="relative bg-[#08080a] py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left: Content */}
          <div className="reveal-up max-w-xl">
            <SectionEyebrow>An Active Startup Ecosystem</SectionEyebrow>
            <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
              Meaningful interaction, by{' '}
              <span className="text-[#d42b92]">design</span>
            </h2>
            <div className="mt-6 space-y-5 text-white/60 text-base sm:text-lg leading-relaxed">
              <p>Studio-I is designed to encourage meaningful interaction.</p>
              <p>
                The space regularly brings together founders, portfolio companies, mentors,
                industry experts and ecosystem partners through meetings, discussions, knowledge
                sessions and community activities.
              </p>
              <p className="text-white font-medium">
                It creates an environment where entrepreneurs do not have to build in isolation.
              </p>
            </div>
          </div>

          {/* Right: Image */}
          <div className="reveal-up rounded-[1.75rem] overflow-hidden border border-white/10 aspect-[16/10] sm:aspect-[4/3] group shadow-2xl">
            <img
              src="/new_images/studio-i-ecosystem.webp"
              alt="Studio-I ecosystem interaction"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Who is it for */}
      <section className="relative bg-[#08080a] py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up max-w-4xl lg:max-w-5xl">
            <SectionEyebrow>Who Is Studio-I For?</SectionEyebrow>
            <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
              Studio-I is suited for
            </h2>
          </div>
          <div className="reveal-up mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {AUDIENCE.map((item, i) => (
              <div
                key={item}
                className="rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-5 hover:border-[#d42b92]/45 transition-colors"
              >
                {/* <div className="text-[#ff8ec5] text-[11px] tracking-[0.2em] uppercase mb-2">
                  {String(i + 1).padStart(2, '0')}
                </div> */}
                <p className="text-white/85 text-sm sm:text-base leading-snug">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* A space that works with you */}
      <section className="relative bg-[#08080a] py-16 lg:py-24 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[640px] h-[280px] rounded-full bg-[#d42b92]/12 blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <SplitSection
            className="reveal-up"
            eyebrow="A Space That Works With You"
            title={
              <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                Across every stage of the{' '}
                <span className="text-[#d42b92]">journey</span>
              </h2>
            }
          >
            <p>
              From focused work and team discussions to stakeholder meetings, mentorship
              conversations and ecosystem interactions, Studio-I provides a professional setting
              for different stages of the entrepreneurial journey.
            </p>
            <p className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">
              Work here. Meet here. Build here. Grow here.
            </p>
          </SplitSection>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#08080a] py-16 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-[#d42b92]/20 via-white/[0.03] to-transparent p-8 sm:p-12 lg:p-14 max-w-4xl overflow-hidden relative">
            <div className="absolute inset-0 opacity-20 pointer-events-none">
              <img
                src={STUDIO_IMAGES[3]?.src || heroImage}
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#08080a] via-[#08080a]/90 to-[#08080a]/40" />
            </div>
            <div className="relative">
              <SectionEyebrow>Become Part of Studio-I</SectionEyebrow>
              <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                Looking for a professional workspace surrounded by an active startup ecosystem?
              </h2>
              <p className="mt-5 text-white/65 text-base sm:text-lg leading-relaxed max-w-2xl">
                Connect with Innovher to explore Studio-I.
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#d42b92]/50 bg-[#d42b92]/15 px-6 py-3 text-sm font-medium text-white hover:bg-[#d42b92] hover:border-[#d42b92] transition-colors"
              >
                Connect with Innovher <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
